const STALE_AFTER_MS = 72 * 60 * 60 * 1000;

const nonEmpty = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

const normalizeKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

export function resolveIntegrationReadinessBadge(providerStatus) {
  if (!providerStatus || typeof providerStatus !== "object") return null;

  const reportingReady =
    providerStatus?.readiness?.ok === true ||
    providerStatus?.readOnlyReady === true ||
    providerStatus?.reportingReady === true ||
    providerStatus?.lifecycle?.reportingAvailable === true;
  const connected = providerStatus?.connected === true || reportingReady;

  if (!connected) return null;

  return {
    connected,
    reportingReady,
    label: reportingReady ? "REPORTING READY" : "CONNECTED",
  };
}

export function getFirstAdvertiserReportValue(source, fields = []) {
  if (!source || typeof source !== "object") return "";

  for (const field of fields) {
    const value = source?.[field];
    if (typeof value === "string" && value.trim()) return value.trim();
  }

  return "";
}

export function hasAdvertiserReportLink(
  source,
  channelKey,
  explicitKeys = [],
  { useGenericScan = true } = {}
) {
  if (!source || typeof source !== "object") return false;
  if (getFirstAdvertiserReportValue(source, explicitKeys)) return true;
  if (!useGenericScan || !channelKey) return false;

  const normalizedChannel = normalizeKey(channelKey);
  return Object.entries(source).some(([key, value]) => {
    if (typeof value !== "string" || !value.trim()) return false;
    const normalizedField = normalizeKey(key);
    return (
      normalizedField.includes(normalizedChannel) &&
      ["link", "url", "report", "looker", "analytics"].some((fragment) =>
        normalizedField.includes(fragment)
      )
    );
  });
}

export function buildSocialPlatformReadinessEntries({
  definitions = [],
  rawAdvertiser = null,
  availablePlatformKeys = [],
  integrationReadinessByPlatform = {},
  metaReportingReady = false,
}) {
  const availableSet = new Set(
    (availablePlatformKeys || []).map((key) => normalizeKey(key)).filter(Boolean)
  );

  return definitions.map((platform) => {
    if (platform.readinessInIntegrationCard) {
      return { ...platform, connected: false, reportingReady: false,
        hasLinkSignal: false, hasSnapshotSignal: false, hasLiveProviderSignal: false,
        hasReadinessSignal: false, statusBadgeLabel: "", reportUrl: "" };
    }
    const primaryReportUrl = getFirstAdvertiserReportValue(
      rawAdvertiser,
      platform.explicitKeys
    );
    const aliasMatch = Array.isArray(platform.aliases)
      ? platform.aliases.find((alias) =>
          hasAdvertiserReportLink(
            rawAdvertiser,
            alias.channelKey,
            alias.explicitKeys || [],
            { useGenericScan: alias.useGenericScan !== false }
          )
        )
      : null;
    const aliasReportUrl = aliasMatch
      ? getFirstAdvertiserReportValue(rawAdvertiser, aliasMatch.explicitKeys || [])
      : "";
    const hasLinkSignal =
      hasAdvertiserReportLink(
        rawAdvertiser,
        platform.key,
        platform.explicitKeys,
        { useGenericScan: platform.useGenericScan !== false }
      ) || Boolean(aliasMatch);
    const lookupKeys = [
      platform.liveKey,
      platform.key,
      ...(Array.isArray(platform.reportingKeys) ? platform.reportingKeys : []),
      ...(Array.isArray(platform.aliases)
        ? platform.aliases.map((alias) => alias?.channelKey)
        : []),
    ]
      .map(normalizeKey)
      .filter(Boolean);
    const hasSnapshotSignal = lookupKeys.some((key) => availableSet.has(key));
    const isMetaLivePlatform =
      platform.liveKey === "facebook" || platform.liveKey === "instagram";
    const hasLiveProviderSignal = isMetaLivePlatform && metaReportingReady;
    const providerKey = normalizeKey(platform.integrationProviderKey);
    const readinessBadge =
      integrationReadinessByPlatform[platform.key] ||
      integrationReadinessByPlatform[providerKey] ||
      null;
    const hasReadinessSignal = Boolean(readinessBadge?.connected);
    const connected =
      hasLiveProviderSignal ||
      hasLinkSignal ||
      hasSnapshotSignal ||
      hasReadinessSignal;
    /*
     * The Meta provider signal establishes CONNECTION, never reporting.
     * A usable Meta credential proves the integration exists; it proves
     * nothing about whether this advertiser's campaigns run Facebook or
     * Instagram. Promoting it to reporting readiness let a connected Meta
     * account light up both publisher cards for advertisers whose governed
     * campaign scope excluded Meta entirely.
     *
     * Reporting readiness needs evidence scoped to the advertiser: a platform
     * present in the governed reporting scope (hasSnapshotSignal, fed by the
     * Optimus platform tabs) or an integration that reports readiness for
     * itself. A connected-but-not-participating Meta surface therefore renders
     * CONNECTED, which is the honest existing state.
     */
    const reportingReady =
      Boolean(readinessBadge?.reportingReady) || hasSnapshotSignal;

    return {
      ...platform,
      connected,
      hasLinkSignal,
      hasSnapshotSignal,
      hasLiveProviderSignal,
      hasReadinessSignal,
      reportingReady,
      statusBadgeLabel: reportingReady
        ? "REPORTING READY"
        : hasLinkSignal
        ? "REPORT LINK"
        : readinessBadge?.label || (connected ? "CONNECTED" : ""),
      reportUrl: primaryReportUrl || aliasReportUrl || "",
    };
  });
}

export function classifyOmniFreshness(value, now = Date.now()) {
  if (!value) return { state: "unknown", label: "Freshness unavailable" };
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) {
    return { state: "unknown", label: "Freshness unavailable" };
  }

  const ageMs = Math.max(0, Number(now) - timestamp);
  const ageHours = Math.floor(ageMs / (60 * 60 * 1000));
  if (ageMs > STALE_AFTER_MS) {
    return {
      state: "stale",
      label: `Stale · ${Math.max(1, Math.floor(ageHours / 24))}d old`,
    };
  }

  return {
    state: "current",
    label: ageHours < 1 ? "Current · under 1h" : `Current · ${ageHours}h old`,
  };
}

function resolveReportingEvidence(entry, topline, reportingError, now) {
  if (reportingError) {
    return {
      state: "error",
      label: "Reporting error",
      freshness: { state: "unknown", label: "Freshness unavailable" },
      primaryMetric: null,
      evidenceState: "A governed reporting request failed independently.",
    };
  }

  const raw = topline?.raw || {};
  const metricCandidates = [
    ["Spend", topline?.spend],
    ["Impressions", topline?.impressions],
    ["Clicks", topline?.clicks],
    ["CTR", topline?.ctr],
  ];
  const primaryMetricPair = metricCandidates.find(([, value]) => nonEmpty(value));
  const hasToplineEvidence =
    Boolean(topline) &&
    (raw?.hasData === true ||
      raw?.snapshotFound === true ||
      metricCandidates.some(([, value]) => nonEmpty(value)));
  const sourceUpdatedAt =
    raw?.sourceUpdatedAt ||
    raw?.latestIngestedAt ||
    raw?.snapshotUpdatedAt ||
    raw?.updatedAt ||
    topline?.updatedAt ||
    null;
  const freshness = classifyOmniFreshness(sourceUpdatedAt, now);

  if (hasToplineEvidence) {
    return {
      state: raw?.snapshotFound === true ? "imported" : "available",
      label:
        raw?.snapshotFound === true
          ? "Imported reporting"
          : "Governed reporting available",
      freshness,
      primaryMetric: primaryMetricPair
        ? { label: primaryMetricPair[0], value: primaryMetricPair[1] }
        : null,
      evidenceState: "Governed topline evidence is available for this destination.",
    };
  }

  /*
   * Reporting readiness, not provider connectivity.
   *
   * hasLiveProviderSignal is the raw Meta provider signal: it means a usable
   * Meta credential exists, not that this advertiser's campaigns run Facebook
   * or Instagram. Accepting it here claimed "Reporting ready" — and asserted
   * the readiness was verified — for advertisers whose governed campaign scope
   * excluded Meta entirely.
   *
   * entry.reportingReady already carries the governed answer: an integration
   * that reports readiness for itself, or a platform present in the governed
   * reporting scope. A connected-but-non-participating surface falls through
   * to the honest unavailable state, matching the CONNECTED badge the card
   * renders for it.
   */
  if (entry?.reportingReady) {
    return {
      state: "ready",
      label: "Reporting ready",
      freshness,
      primaryMetric: null,
      evidenceState: "Read-only reporting readiness is reported by the integration; delivery evidence is not loaded.",
    };
  }

  if (entry?.hasSnapshotSignal) {
    return {
      state: "imported",
      label: "Imported reporting available",
      freshness,
      primaryMetric: null,
      evidenceState: "A governed reporting snapshot is available.",
    };
  }

  if (entry?.hasLinkSignal || entry?.reportUrl) {
    return {
      state: "report_link",
      label: "Report-link only",
      freshness,
      primaryMetric: null,
      evidenceState: "A report surface is linked; delivery evidence is not available here.",
    };
  }

  return {
    state: "unavailable",
    label: "Reporting unavailable",
    freshness,
    primaryMetric: null,
    evidenceState: "No governed reporting evidence is available for this destination.",
  };
}

function resolveAudienceEvidence(platformKey, audienceByPlatform, sharedAudienceResolution) {
  const platformResolution = audienceByPlatform?.[platformKey] || null;
  const hasAudienceEvidence = (resolution) => {
    if (!resolution || typeof resolution !== "object") return false;
    if (normalizeKey(resolution.tier) === "unavailable") return false;

    return Boolean(
      resolution?.card?.available === true ||
        ["live", "manual", "planner_context"].includes(normalizeKey(resolution.tier)) ||
        [
          resolution.audienceSummary,
          resolution.primaryAudienceNarrative,
          resolution.clientFacingPrompt,
          resolution.recommendedAction,
        ].some(nonEmpty) ||
        (Array.isArray(resolution.strongestSegments) &&
          resolution.strongestSegments.some(nonEmpty)) ||
        (Array.isArray(resolution.emergingSegments) &&
          resolution.emergingSegments.some(nonEmpty))
    );
  };

  if (hasAudienceEvidence(platformResolution)) {
    const freshness = classifyOmniFreshness(
      platformResolution?.updatedAt ||
        platformResolution?.snapshot?.updatedAt ||
        platformResolution?.card?.updatedAt
    );
    return {
      state: "platform_specific",
      label: "Platform-specific synthesis",
      freshness,
    };
  }

  if (hasAudienceEvidence(sharedAudienceResolution)) {
    const freshness = classifyOmniFreshness(
      sharedAudienceResolution?.updatedAt ||
        sharedAudienceResolution?.snapshot?.updatedAt ||
        sharedAudienceResolution?.card?.updatedAt
    );
    return {
      state: "shared",
      label: "Shared synthesis context",
      freshness,
    };
  }

  return {
    state: "unavailable",
    label: "Audience synthesis unavailable",
    freshness: { state: "unknown", label: "Freshness unavailable" },
  };
}

function buildLimitation({ entry, reporting, audience }) {
  if (!entry?.connected && reporting.state === "unavailable") {
    return "No verified connection or governed reporting evidence is available.";
  }
  if (reporting.state === "error") {
    return "Reporting could not be resolved; connection state is preserved separately.";
  }
  if (reporting.state === "report_link") {
    return "A report link exists, but comparable delivery metrics are unavailable here.";
  }
  if (reporting.freshness.state === "stale") {
    return "Reporting evidence is stale and should be refreshed before allocation decisions.";
  }
  if (audience.state === "unavailable") {
    return "No governed audience-synthesis record is available for this platform.";
  }
  if (audience.state === "shared") {
    return "Audience context is shared and is not platform-specific.";
  }
  return "Available evidence is directional and does not represent deduplicated reach.";
}

function buildRecommendation({ route, reporting, audience }) {
  if (reporting.state === "error") return "Retry the read-only reporting check.";
  if (reporting.freshness.state === "stale") return "Review source freshness.";
  if (reporting.state === "unavailable") return "Review reporting readiness.";
  if (reporting.state === "report_link") return "Open the platform report.";
  if (audience.state === "unavailable") return "Review audience evidence.";
  return `Review governed analytics at ${route}.`;
}

export function buildOmniPlatformIntelligenceModel({
  definitions = [],
  socialEntries = [],
  omniEntries = [],
  toplinesByPlatform = {},
  reportingErrorsByPlatform = {},
  audienceByPlatform = {},
  sharedAudienceResolution = null,
  advertiserSelected = false,
  role = "client",
  now = Date.now(),
}) {
  const socialKeys = new Set(socialEntries.map((entry) => entry.key));
  const entryMap = new Map(
    [...socialEntries, ...omniEntries].map((entry) => [entry.key, entry])
  );

  const platforms = definitions.map((definition) => {
    try {
      const entry = { ...definition, ...(entryMap.get(definition.key) || {}) };
      const reporting = resolveReportingEvidence(
        entry,
        toplinesByPlatform?.[definition.key],
        reportingErrorsByPlatform?.[definition.key],
        now
      );
      const audience = resolveAudienceEvidence(
        definition.key,
        audienceByPlatform,
        sharedAudienceResolution
      );
      const connected = Boolean(entry.connected);
      const group = socialKeys.has(definition.key)
        ? "Social Platform"
        : "Omni Channel";
      const model = {
        key: definition.key,
        label: definition.label,
        displayLabel: definition.tileLabel || definition.label,
        image: definition.image || null,
        group,
        route: definition.route,
        connected,
        // "connected" here can be satisfied by a saved report link alone; it is a signal, not a Cphere verification.
        connectionLabel: connected ? "Connection signal" : "No connection signal",
        reporting,
        audience,
        freshness: reporting.freshness,
        completeness:
          reporting.state === "available" && audience.state === "platform_specific"
            ? "complete"
            : reporting.state === "unavailable" && audience.state === "unavailable"
            ? "missing"
            : "partial",
        limitation: buildLimitation({ entry, reporting, audience }),
        recommendedInvestigation: buildRecommendation({
          route: definition.route,
          reporting,
          audience,
        }),
      };

      if (role === "superadmin") {
        model.evidenceState = reporting.evidenceState;
      }

      return model;
    } catch {
      return {
        key: definition.key,
        label: definition.label,
        displayLabel: definition.tileLabel || definition.label,
        image: definition.image || null,
        group: socialKeys.has(definition.key) ? "Social Platform" : "Omni Channel",
        route: definition.route,
        connected: false,
        connectionLabel: "Connection state unavailable",
        reporting: {
          state: "error",
          label: "Evidence resolution error",
          primaryMetric: null,
        },
        audience: { state: "unavailable", label: "Audience synthesis unavailable" },
        freshness: { state: "unknown", label: "Freshness unavailable" },
        completeness: "missing",
        limitation: "This platform record could not be resolved independently.",
        recommendedInvestigation: "Review the platform analytics page.",
      };
    }
  });

  const summary = {
    represented: platforms.length,
    connected: platforms.filter((platform) => platform.connected).length,
    reporting: platforms.filter((platform) =>
      ["available", "imported", "ready"].includes(platform.reporting.state)
    ).length,
    audienceSpecific: platforms.filter(
      (platform) => platform.audience.state === "platform_specific"
    ).length,
    sharedAudience: platforms.filter(
      (platform) => platform.audience.state === "shared"
    ).length,
    stale: platforms.filter((platform) => platform.freshness.state === "stale").length,
    missing: platforms.filter((platform) => platform.completeness === "missing").length,
    errors: platforms.filter((platform) => platform.reporting.state === "error").length,
  };

  const observed = advertiserSelected
    ? `${summary.connected} of ${summary.represented} destinations have a verified connection signal; ${summary.reporting} have governed reporting or readiness evidence.`
    : "Choose an advertiser to resolve connection, reporting, and audience evidence.";
  const interpretation = advertiserSelected
    ? `${summary.missing} destinations currently lack both reporting and platform-specific audience evidence. Missing evidence is not treated as poor performance.`
    : "Portfolio evidence remains unavailable until an authorized advertiser is selected.";
  const recommendedInvestigation = summary.errors
    ? `Review ${summary.errors} reporting error${summary.errors === 1 ? "" : "s"} before using the portfolio for allocation decisions.`
    : summary.stale
    ? `Review ${summary.stale} stale source${summary.stale === 1 ? "" : "s"} before using the portfolio for allocation decisions.`
    : summary.missing
    ? "Review destinations with missing governed reporting before making cross-platform comparisons."
    : "Review platform-level evidence before making allocation decisions.";

  return {
    platforms,
    summary,
    analysis: {
      observed,
      interpretation,
      recommendedInvestigation,
      confidence: `${summary.reporting} of ${summary.represented} destinations currently contribute governed reporting or readiness evidence.`,
    },
    disclosure:
      "Composed from current governed platform reporting, readiness, and audience-synthesis records. No AI run, platform write, deduplicated reach, audience overlap, or causal claim is produced.",
  };
}
